import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FeedbackType } from '../types';
import type { ContextItem } from '../../../services/chatApi';

const backdrop: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const card: React.CSSProperties = { width: 420, maxWidth: '92%', background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' };

export function TitleModal({ open, value, onChange, onClose, onSave, inputRef }: {
  open: boolean; value: string; onChange: (v: string) => void; onClose: () => void; onSave: () => void; inputRef?: React.Ref<HTMLInputElement>;
}) {
  if (!open) return null;
  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '4px 0 10px' }}>Chatnaam bewerken</h3>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(); }}
          placeholder="Chat titel"
          style={{ width: '100%', border: '1px solid #0077c8', borderRadius: 8, padding: 10, fontSize: 14 }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff' }}>Annuleer</button>
          <button onClick={onSave} style={{ padding: '8px 10px', borderRadius: 8, border: 0, background: '#1f74ff', color: '#fff' }}>Opslaan</button>
        </div>
      </div>
    </div>
  );
}

export function FeedbackModal({ open, type, value, onChange, onClose, onSend }: {
  open: boolean; type: FeedbackType; value: string; onChange: (v: string) => void; onClose: () => void; onSend: () => void;
}) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '4px 0 10px' }}>{t('chat.feedback.helpful')}</h3>
        <div style={{ color: '#555', marginBottom: 8 }}>
          {type === 'up' ? t('chat.feedback.good') : t('chat.feedback.improve')}
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={t('chat.feedback.comment')}
          style={{ width: '100%', border: '1px solid #ddd', borderRadius: 8, padding: 10, fontSize: 14 }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff' }}>
            {t('chat.feedback.cancel')}
          </button>
          <button onClick={onSend} style={{ padding: '8px 10px', borderRadius: 8, border: 0, background: '#1f74ff', color: '#fff' }}>
            {t('chat.feedback.send')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SourcesModal({ sources, onClose }: { sources: ContextItem[] | null; onClose: () => void; }) {
  const { t } = useTranslation();

  const uniqueSources = React.useMemo(() => {
    const list = sources ?? [];
    const seen = new Set<string>();
    return list.filter((source) => {
      const key =
        source.id ||
        source.fileId ||
        source.displayUrl ||
        source.sourceUrl ||
        source.url ||
        source.fileName ||
        source.title;
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [sources]);

  if (!sources) return null;

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={{ ...card, width: 640 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '4px 0 10px' }}>{t('chat.sources.title')}</h3>
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {uniqueSources.map((source) => {
            const title = source.title || source.fileName || 'Onbekende bron';
            const description = source.description || source.documentType || '';
            const languageBadge = (source.language || 'NL').toUpperCase();
            const href = source.displayUrl || source.sourceUrl || source.url || undefined;
            const cardKey = source.id || source.fileId || href || title;

            return (
              <div
                key={cardKey}
                style={{
                  background: '#f7f9fc',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
                  marginBottom: 14,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#23385d', marginBottom: 4 }}>
                      {title}
                    </div>
                    {description && (
                      <div style={{ fontSize: 13, color: '#5f6b7c' }}>
                        {description}
                      </div>
                    )}
                  </div>

                  <span
                    style={{
                      alignSelf: 'flex-start',
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: '#e9eef8',
                      color: '#394868',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {languageBadge}
                  </span>
                </div>

                {href && (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      alignSelf: 'flex-start',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: '#fff',
                      border: '1px solid #dde3f0',
                      color: '#1f4e9d',
                      fontSize: 13,
                      textDecoration: 'none',
                      fontWeight: 600,
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 16 }}>📄</span>
                    Open document
                  </a>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff' }}>
            {t('chat.sources.close')}
          </button>
        </div>
      </div>
    </div>
  );
}